'use client'
import { SetStateAction, useCallback, useEffect, useState } from "react";
import { FtpContents, SelectedItem, RouteType } from "@/types";
import { listDirectory } from "@/app/api";
import path from "path-browserify";

export function useFTP(routeType: RouteType, initialPath: string = '/') {
    const [currentPath, setCurrentPath] = useState(initialPath);
    const [contents, setContents] = useState<FtpContents[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);

    const loadDirectory = useCallback(async (path: string = '', clientUserName?: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await listDirectory(routeType, path, clientUserName)
            setCurrentPath(data.path)
            setContents(data.contents)
            setSelectedItems([]);
        } catch (err) {
            setError('Falha ao carregar diretório');
            console.log('Erro ao carregar diretório:', err);
        } finally {
            setIsLoading(false);
            setError(null);
        }
    }, [routeType])


    const handleDownload = useCallback(async (filePath: string = '') => {
        setIsDownloading(true);
        setError(null);
        try {
            const res = await fetch(`/sistema/api/ftp?routeType=${routeType}&path=${filePath}`);
            if (!res.ok) {
                console.log(`Erro ${res.status}: ${res.statusText}`);
            }

            console.log('chegou aqui')
            const filename = res.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'arquivo'
            const blob = await res.blob();
            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.style.display = 'none';
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            }, 1000);
        } catch (err) {
            setError(`Falha ao baixar arquivo, ${err}`);
        } finally {
            setIsDownloading(false);
            setError(null);
        }
    }, [routeType])


    const downloadSelected = useCallback(async () => {
        if (selectedItems.length === 0) return
        setIsDownloading(true);
        try {
            for (const item of selectedItems) {
                await handleDownload(item.path)
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        } catch (err) {
            setError(`Falha ao baixar arquivos selecionados, ${err}`);
        } finally {
            setIsDownloading(false);
        }
    }, [selectedItems, handleDownload])

    const toggleAllItems = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const allItems: SetStateAction<SelectedItem[]> = contents.map(item => ({
                routeType,
                path: path.posix.join(currentPath, item.name),
                type: item.type === 'directory' ? 'directory' : 'file'
            }));
            setSelectedItems(allItems)
        } else {
            setSelectedItems([])
        }
    }, [contents, currentPath, routeType])

    const toggleItem = useCallback((item: { name: string, type: string }, e: React.MouseEvent) => {
        e.stopPropagation();
        const itemPath = path.posix.join(currentPath, item.name)
        const itemType = ((item.type === 'directory' || item.type === 'dir') ? 'directory' : 'file')
        setSelectedItems(prev => {
            const exists = prev.some(i => i.path === itemPath)
            return exists
                ? prev.filter(i => i.path !== itemPath)
                : [...prev, { routeType, path: itemPath, type: itemType }]
        })
    }, [currentPath, routeType]);

    const navigateTo = useCallback((item: { name: string; type: string }) => {
        if (item.type === 'directory' || item.type === 'dir') {
            loadDirectory(path.posix.join(routeType, currentPath, item.name))
        }
    }, [currentPath, loadDirectory])

    useEffect(() => {
        loadDirectory();
    }, [loadDirectory]);

    return {
        currentPath,
        contents,
        isLoading,
        isDownloading,
        error,
        selectedItems,
        loadDirectory,
        downloadSelected,
        toggleAllItems,
        toggleItem,
        navigateTo
    };
}